import { clsx } from 'clsx';
import { ABILITY_NAMES, type AbilityName, type AbilityScoreMethod } from '@character-creator/types';
import {
  calculateModifier,
  calculateRacialBonuses,
  POINT_BUY_COSTS,
  POINT_BUY_TOTAL,
  POINT_BUY_MIN,
  POINT_BUY_MAX,
  STANDARD_ARRAY,
} from '@character-creator/core';
import { getRace, getASILevels, getClassForRuleset } from '@character-creator/data';
import { useCharacterCreationStore, type AsiChoice } from '../../../stores/characterCreationStore';
import { generateRandomAbilityScores } from '../../../utils/randomCharacter';
import { DiceIcon } from '../../ui/Icons';
import { AsiSelector } from '../AsiSelector';

export function AbilityScoresStep() {
  const {
    draft,
    setAbilityScoreMethod,
    setAbilityScore,
    setAbilityScores,
    resetAbilityScores,
    setAsiChoice,
    clearAsiChoice,
  } = useCharacterCreationStore();

  const handleRollDice = () => {
    const scores = generateRandomAbilityScores();
    // Order matters: setAbilityScoreMethod resets scores to 8s,
    // so we must call it first, then set the rolled scores
    setAbilityScoreMethod('manual');
    setAbilityScores(scores);
  };

  const race = draft.raceId ? getRace(draft.raceId) : null;
  const subrace = race?.subraces?.find(s => s.id === draft.subraceId);
  const racialBonuses = calculateRacialBonuses(race, subrace);

  // Point buy validation
  const pointsSpent = Object.values(draft.abilityScores).reduce(
    (sum, score) => sum + (POINT_BUY_COSTS[score] ?? 0),
    0
  );
  const pointsRemaining = POINT_BUY_TOTAL - pointsSpent;

  // Standard array: track which scores have been assigned
  const usedScores = Object.values(draft.abilityScores);

  // Check if any scores have been modified from default
  const hasData = Object.values(draft.abilityScores).some(score => score !== 8);

  // Compute per-ability ASI bonuses by aggregating all classLevel asiChoices
  const asiBonuses: Record<AbilityName, number> = {
    strength: 0, dexterity: 0, constitution: 0,
    intelligence: 0, wisdom: 0, charisma: 0,
  };
  for (const cl of draft.classLevels) {
    if (cl.asiChoices) {
      for (const choice of Object.values(cl.asiChoices)) {
        if (choice.type === 'ability-increase') {
          for (const inc of choice.increases) {
            asiBonuses[inc.ability as AbilityName] += inc.amount;
          }
        }
      }
    }
  }

  // Compute after-racial scores for ASI cap validation
  const afterRacialScores = Object.fromEntries(
    ABILITY_NAMES.map(a => [a, draft.abilityScores[a] + (racialBonuses[a] || 0)])
  ) as Record<AbilityName, number>;

  // ASI data for the section below
  const characterLevel = draft.classLevels.length > 0
    ? draft.classLevels.reduce((sum, cl) => sum + cl.level, 0)
    : 1;

  const allAsiEntries = draft.classLevels.flatMap(cl => {
    const cls = getClassForRuleset(cl.classId, draft.rulesetId);
    return getASILevels(cl.classId)
      .filter(l => l <= cl.level)
      .map(asiLevel => ({
        classId: cl.classId,
        className: cls?.name ?? cl.classId,
        asiLevel,
        currentChoice: cl.asiChoices?.[asiLevel] ?? null,
      }));
  });

  const hasMultipleClasses = draft.classLevels.length > 1;

  // Determine the first ASI level from any class
  const firstAsiLevel = draft.classLevels.length > 0
    ? Math.min(...draft.classLevels.map(cl => getASILevels(cl.classId)[0] ?? 20))
    : 4;

  const handleAsiSelect = (classId: string, asiLevel: number, choice: AsiChoice | null) => {
    if (choice) {
      setAsiChoice(classId, asiLevel, choice);
    } else {
      clearAsiChoice(classId, asiLevel);
    }
  };

  return (
    <div className="animate-fade-in">
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <h2 className="font-display text-2xl font-bold text-text-primary mb-2">
            Ability Scores
          </h2>
          <p className="text-text-secondary">
            Assign your character's core attributes. These define your physical and mental capabilities.
          </p>
        </div>
        {hasData && (
          <button
            type="button"
            onClick={resetAbilityScores}
            className="flex-shrink-0 px-3 py-1.5 text-sm rounded-lg text-text-muted hover:text-error hover:bg-error/10 transition-colors"
          >
            Clear
          </button>
        )}
      </div>

      {/* Method Selection */}
      <div className="card p-4 mb-6">
        {/* Primary methods */}
        <div className="flex flex-wrap gap-2">
          <MethodButton
            method="point-buy"
            label="Point Buy"
            currentMethod={draft.abilityScoreMethod}
            onClick={() => {
              setAbilityScoreMethod('point-buy');
              resetAbilityScores();
            }}
          />
          <MethodButton
            method="standard-array"
            label="Standard Array"
            currentMethod={draft.abilityScoreMethod}
            onClick={() => {
              setAbilityScoreMethod('standard-array');
              resetAbilityScores();
            }}
          />
        </div>
        {/* Secondary methods */}
        <div className="flex flex-wrap items-center gap-3 mt-3 pt-3 border-t border-border-default">
          <span className="text-xs text-text-muted">or</span>
          <button
            type="button"
            onClick={handleRollDice}
            className={clsx(
              'text-sm transition-colors flex items-center gap-1.5',
              draft.abilityScoreMethod === 'manual' && Object.values(draft.abilityScores).some(s => s !== 8)
                ? 'text-accent-gold'
                : 'text-text-muted hover:text-text-secondary'
            )}
          >
            <DiceIcon className="w-3.5 h-3.5" />
            Roll Dice (4d6)
          </button>
          <span className="text-text-muted/50">·</span>
          <button
            type="button"
            onClick={() => setAbilityScoreMethod('manual')}
            className={clsx(
              'text-sm transition-colors',
              draft.abilityScoreMethod === 'manual'
                ? 'text-accent-gold'
                : 'text-text-muted hover:text-text-secondary'
            )}
          >
            Manual Entry
          </button>
        </div>
      </div>

      {/* Point Buy Info */}
      {draft.abilityScoreMethod === 'point-buy' && (
        <div className={clsx(
          'card p-4 mb-6 text-center',
          pointsRemaining === 0 ? 'border-success' : pointsRemaining < 0 ? 'border-error' : ''
        )}>
          <div className="text-3xl font-display font-bold text-accent-gold">
            {pointsRemaining}
          </div>
          <div className="text-sm text-text-muted">Points Remaining</div>
        </div>
      )}

      {/* Standard Array Info */}
      {draft.abilityScoreMethod === 'standard-array' && (
        <div className="card p-4 mb-6">
          <div className="text-sm text-text-muted mb-2">Available Scores:</div>
          <div className="flex flex-wrap gap-2">
            {STANDARD_ARRAY.map((score, i) => (
              <span
                key={i}
                className={clsx(
                  'px-3 py-1 rounded text-sm font-medium',
                  usedScores.includes(score)
                    ? 'bg-accent-gold/20 text-accent-gold'
                    : 'bg-background-elevated text-text-primary'
                )}
              >
                {score}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Ability Scores Grid */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
        {ABILITY_NAMES.map((ability) => (
          <AbilityScoreCard
            key={ability}
            ability={ability}
            baseScore={draft.abilityScores[ability]}
            racialBonus={racialBonuses[ability] || 0}
            asiBonus={asiBonuses[ability]}
            method={draft.abilityScoreMethod}
            onScoreChange={(value) => setAbilityScore(ability, value)}
            pointsRemaining={pointsRemaining}
          />
        ))}
      </div>

      {/* ASI/Feat Section */}
      <div className="mt-8">
        <h3 className="font-display text-lg font-bold text-text-primary mb-1">
          Ability Score Improvements
        </h3>
        <p className="text-sm text-text-secondary mb-4">
          As you gain class levels, you earn Ability Score Improvements (ASI) or feats.
        </p>

        {draft.classLevels.length === 0 ? (
          <p className="text-sm text-text-muted italic">
            Select a class to unlock Ability Score Improvements.
          </p>
        ) : allAsiEntries.length === 0 ? (
          <p className="text-sm text-text-muted italic">
            Reach class level {firstAsiLevel} to unlock your first Ability Score Improvement.
          </p>
        ) : hasMultipleClasses ? (
          /* Multiclass: group by class */
          <div className="space-y-6">
            {draft.classLevels.map(cl => {
              const entries = allAsiEntries.filter(e => e.classId === cl.classId);
              if (entries.length === 0) return null;
              return (
                <div key={cl.classId}>
                  <h4 className="text-sm font-semibold text-text-primary mb-3">
                    {entries[0].className}
                  </h4>
                  <div className="space-y-3">
                    {entries.map(entry => (
                      <div key={`${entry.classId}-${entry.asiLevel}`} className="card p-4">
                        <AsiSelector
                          asiLevel={entry.asiLevel}
                          currentChoice={entry.currentChoice}
                          abilityScores={afterRacialScores}
                          characterLevel={characterLevel}
                          onSelect={(choice) => handleAsiSelect(entry.classId, entry.asiLevel, choice)}
                          compact={!!entry.currentChoice}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          /* Single class: render flat */
          <div className="space-y-3">
            {allAsiEntries.map(entry => (
              <div key={`${entry.classId}-${entry.asiLevel}`} className="card p-4">
                <AsiSelector
                  asiLevel={entry.asiLevel}
                  currentChoice={entry.currentChoice}
                  abilityScores={afterRacialScores}
                  characterLevel={characterLevel}
                  onSelect={(choice) => handleAsiSelect(entry.classId, entry.asiLevel, choice)}
                  compact={!!entry.currentChoice}
                />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

interface MethodButtonProps {
  method: AbilityScoreMethod;
  label: string;
  currentMethod: AbilityScoreMethod;
  onClick: () => void;
}

function MethodButton({ method, label, currentMethod, onClick }: MethodButtonProps) {
  const isActive = method === currentMethod;
  return (
    <button
      type="button"
      onClick={onClick}
      className={clsx(
        'px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200',
        isActive
          ? 'bg-accent-gold text-background-primary'
          : 'bg-background-secondary text-text-primary hover:bg-background-elevated'
      )}
    >
      {label}
    </button>
  );
}

interface AbilityScoreCardProps {
  ability: AbilityName;
  baseScore: number;
  racialBonus: number;
  asiBonus?: number;
  method: AbilityScoreMethod;
  onScoreChange: (value: number) => void;
  pointsRemaining: number;
}

function AbilityScoreCard({
  ability,
  baseScore,
  racialBonus,
  asiBonus = 0,
  method,
  onScoreChange,
  pointsRemaining,
}: AbilityScoreCardProps) {
  const totalScore = baseScore + racialBonus + asiBonus;
  const modifier = calculateModifier(totalScore);
  const modifierStr = modifier >= 0 ? `+${modifier}` : `${modifier}`;

  const canIncrease = method === 'point-buy'
    ? baseScore < POINT_BUY_MAX && (POINT_BUY_COSTS[baseScore + 1] - POINT_BUY_COSTS[baseScore]) <= pointsRemaining
    : method === 'manual'
      ? baseScore < 20
      : false;

  const canDecrease = method === 'point-buy'
    ? baseScore > POINT_BUY_MIN
    : method === 'manual'
      ? baseScore > 1
      : false;

  return (
    <div className="stat-block">
      <div className="text-center mb-3">
        <div className="text-sm font-semibold text-text-primary uppercase tracking-wider">
          {ability}
        </div>
      </div>

      <div className="flex items-center justify-center gap-3 mb-3">
        {(method === 'point-buy' || method === 'manual') && (
          <button
            type="button"
            onClick={() => onScoreChange(baseScore - 1)}
            disabled={!canDecrease}
            className={clsx(
              'w-8 h-8 rounded-full flex items-center justify-center transition-all',
              canDecrease
                ? 'bg-background-secondary text-text-primary hover:bg-background-elevated'
                : 'bg-background-secondary text-text-muted cursor-not-allowed'
            )}
          >
            -
          </button>
        )}

        {method === 'standard-array' ? (
          <div className="text-center">
            <select
              value={baseScore}
              onChange={(e) => onScoreChange(parseInt(e.target.value, 10))}
              className="input w-20 text-center text-lg font-bold"
            >
              <option value={8}>8</option>
              {STANDARD_ARRAY.map((score) => (
                <option key={score} value={score}>
                  {score}
                </option>
              ))}
            </select>
            <div className="text-xs text-text-muted mt-1">Base</div>
          </div>
        ) : (
          <div className="w-16 text-center">
            <div className="text-3xl font-display font-bold text-text-primary">
              {baseScore}
            </div>
            <div className="text-xs text-text-muted">Base</div>
          </div>
        )}

        {(method === 'point-buy' || method === 'manual') && (
          <button
            type="button"
            onClick={() => onScoreChange(baseScore + 1)}
            disabled={!canIncrease}
            className={clsx(
              'w-8 h-8 rounded-full flex items-center justify-center transition-all',
              canIncrease
                ? 'bg-background-secondary text-text-primary hover:bg-background-elevated'
                : 'bg-background-secondary text-text-muted cursor-not-allowed'
            )}
          >
            +
          </button>
        )}
      </div>

      {/* Calculation Flow: Base + Racial + ASI = Total */}
      {(racialBonus !== 0 || asiBonus !== 0) ? (
        <div className="flex items-center justify-center gap-2 mb-3 text-sm">
          <div className="flex flex-col items-center">
            <div className="font-mono font-semibold text-text-primary">{baseScore}</div>
            <div className="text-[0.625rem] text-text-muted uppercase tracking-wider">Base</div>
          </div>
          {racialBonus !== 0 && (
            <>
              <div className="text-text-muted text-base mb-3">+</div>
              <div className="flex flex-col items-center">
                <div className="px-2 py-0.5 bg-accent-gold/15 border border-accent-gold/30 rounded font-mono font-semibold text-accent-gold">
                  {racialBonus > 0 ? '+' : ''}{racialBonus}
                </div>
                <div className="text-[0.625rem] text-text-muted uppercase tracking-wider">Racial</div>
              </div>
            </>
          )}
          {asiBonus !== 0 && (
            <>
              <div className="text-text-muted text-base mb-3">+</div>
              <div className="flex flex-col items-center">
                <div className="px-2 py-0.5 bg-success/15 border border-success/30 rounded font-mono font-semibold text-success">
                  +{asiBonus}
                </div>
                <div className="text-[0.625rem] text-text-muted uppercase tracking-wider">ASI</div>
              </div>
            </>
          )}
          <div className="text-text-muted text-base mb-3">=</div>
          <div className="flex flex-col items-center">
            <div className="font-mono font-bold text-accent-gold text-lg">{totalScore}</div>
            <div className="text-[0.625rem] text-text-muted uppercase tracking-wider">Total</div>
          </div>
        </div>
      ) : (
        /* Total Score (when no racial bonus and no ASI bonus) */
        <div className="text-center mb-3">
          <div className="text-2xl font-display font-bold text-accent-gold">
            {totalScore}
          </div>
          <div className="text-xs text-text-muted uppercase tracking-wider">Total Score</div>
        </div>
      )}

      {/* Modifier Section - Clearly Separated */}
      <div className="border-t border-border-default pt-3 mt-3">
        <div className="flex items-center justify-center gap-2">
          <div className="flex items-center gap-2 px-3 py-1.5 bg-background-elevated rounded-md">
            <div className={clsx(
              'text-xl font-mono font-bold',
              modifier > 0 ? 'text-success' : modifier < 0 ? 'text-error' : 'text-text-primary'
            )}>
              {modifierStr}
            </div>
            <div className="text-xs text-text-secondary">to rolls</div>
          </div>
        </div>
      </div>

      {method === 'point-buy' && (
        <div className="text-center mt-2 text-xs text-text-muted">
          Cost: {POINT_BUY_COSTS[baseScore]} pts
        </div>
      )}
    </div>
  );
}
